import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Select,
  SelectItem,
  Textarea
} from "@heroui/react";
import { motion } from "framer-motion";
import { Home, Link2, HelpCircle, Settings, Palette } from "lucide-react";

const NAV_ITEMS = [
  { key: "/", label: "Temunaw", icon: Home },
  { key: "/panunnuan/", label: "Panunnuan", icon: Link2 },
  { key: "/sikawma/", label: "Sikawma", icon: HelpCircle },
  { key: "/settings/", label: "tulis", icon: Palette },
  { key: "/admin/services/", label: "Cover Admin", icon: Settings }
];

const THEME_OPTIONS = [
  { id: "theme1", label: "紫藍深色", desc: "預設配色" },
  { id: "theme2", label: "海龜 pennu", desc: "黃綠到藍綠" },
  { id: "theme3", label: "粉彩", desc: "粉藍粉黃到粉紅" },
  { id: "theme4", label: "水藍色", desc: "天空藍到水藍" }
];

const SERVICE_STATUS_OPTIONS = [
  { key: "active", label: "active" },
  { key: "coming_soon", label: "coming_soon" }
];

const THEME_STORAGE_KEY = "kaput_theme";

export function App() {
  const [path, setPath] = useState(normalizePath(window.location.pathname));
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_STORAGE_KEY) || "theme1");
  const [identity, setIdentity] = useState(null);
  const [services, setServices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [logs, setLogs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [wsState, setWsState] = useState("idle");
  const [actionError, setActionError] = useState("");
  const [gameResult, setGameResult] = useState("");
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [serviceDrafts, setServiceDrafts] = useState({});
  const [form, setForm] = useState({
    registerEmail: "",
    registerName: "",
    registerPassword: "",
    loginEmail: "",
    loginPassword: "",
    roomName: "",
    answerTimeLimit: "30",
    maxMistakes: "3",
    roomId: "",
    joinRoomCode: "",
    gameId: "",
    phrase: "",
    bootstrapEmail: "",
    bootstrapKey: "",
    guestNickname: ""
  });
  const { isOpen: isNicknameOpen, onOpen: onNicknameOpen, onClose: onNicknameClose } = useDisclosure();
  const { isOpen: isCreateRoomOpen, onOpen: onCreateRoomOpen, onClose: onCreateRoomClose } = useDisclosure();

  const wsUrl = useMemo(() => {
    if (!form.roomId) return "";
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${window.location.host}/api/ws/rooms/${form.roomId}`;
  }, [form.roomId]);

  useEffect(() => {
    const onPop = () => setPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    void boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function boot() {
    await refreshMe();
    await refreshServices();
    if (path.startsWith("/panunnuan")) {
      await refreshRooms();
    }
  }

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function applyTheme(themeId) {
    setTheme(themeId);
    document.documentElement.setAttribute("data-theme", themeId);
    localStorage.setItem(THEME_STORAGE_KEY, themeId);
  }

  function appendLog(message) {
    const row = `[${new Date().toISOString()}] ${message}`;
    setLogs((prev) => [row, ...prev].slice(0, 120));
  }

  async function runAction(label, fn) {
    try {
      setActionError("");
      await fn();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message === "unauthorized") {
        await refreshMe();
        try {
          await fn();
          return;
        } catch (retryError) {
          const retryMessage = retryError instanceof Error ? retryError.message : String(retryError);
          setActionError(`${label}: ${mapActionError(retryMessage)}`);
          appendLog(`error ${label}: ${mapActionError(retryMessage)}`);
          return;
        }
      }
      const normalized = mapActionError(message);
      setActionError(`${label}: ${normalized}`);
      appendLog(`error ${label}: ${normalized}`);
    }
  }

  function navigate(nextPath) {
    const normalized = normalizePath(nextPath);
    history.pushState({}, "", normalized);
    setPath(normalized);
    if (normalized.startsWith("/panunnuan")) {
      void refreshMe();
      void refreshRooms();
    }
  }

  async function refreshMe() {
    const res = await api("GET", "/api/me");
    setIdentity(res.identity || null);
    appendLog(`identity=${res.identity?.type || "-"}`);
  }

  async function refreshServices() {
    const res = await api("GET", "/api/services");
    const list = res.services || [];
    setServices(list);
    setServiceDrafts(
      Object.fromEntries(
        list.map((item) => [
          item.serviceSlug,
          {
            title: item.title || "",
            subtitle: item.subtitle || "",
            description: item.description || "",
            status: item.status || "coming_soon",
            sortOrder: String(item.sortOrder ?? 0)
          }
        ])
      )
    );
  }

  async function refreshRooms() {
    const res = await api("GET", "/api/rooms");
    setRooms(res.rooms || []);
  }

  async function register() {
    const email = form.registerEmail || form.loginEmail;
    const password = form.registerPassword || form.loginPassword;
    const displayName = form.registerName || (email.includes("@") ? email.split("@")[0] : "Player");
    await api("POST", "/api/auth/register", {
      email,
      displayName,
      password
    });
    appendLog("registered");
    await refreshMe();
  }

  async function login() {
    await api("POST", "/api/auth/login", {
      email: form.loginEmail,
      password: form.loginPassword
    });
    appendLog("logged in");
    patchForm("bootstrapEmail", form.loginEmail);
    await refreshMe();
  }

  async function logout() {
    await api("POST", "/api/auth/logout");
    appendLog("logged out");
    await refreshMe();
  }

  async function updateGuestNickname() {
    const name = String(form.guestNickname || "").trim().slice(0, 24);
    if (!name) throw new Error("invalid_input");
    await api("PATCH", "/api/me/guest", { displayName: name });
    appendLog(`guest nickname: ${name}`);
    onNicknameClose();
    await refreshMe();
  }

  async function createRoom() {
    setIsCreatingRoom(true);
    try {
      const res = await api("POST", "/api/rooms", {
        name: form.roomName,
        answerTimeLimit: Number(form.answerTimeLimit || 30),
        maxMistakes: Number(form.maxMistakes || 3),
        topicFilters: []
      });
      patchForm("roomId", res.roomId);
      appendLog(`created room ${res.roomId}`);
      await refreshRooms();
      onCreateRoomClose();
      navigate(`/panunnuan/room/${res.roomId}`);
    } finally {
      setIsCreatingRoom(false);
    }
  }

  async function joinByCode() {
    const roomId = String(form.joinRoomCode || "").trim();
    if (!roomId) {
      throw new Error("room_code_required");
    }
    await roomAction("join", roomId);
  }

  async function roomAction(action, roomId) {
    if (action === "join") {
      await api("POST", `/api/rooms/${roomId}/join`);
      patchForm("roomId", roomId);
      appendLog(`joined ${roomId}`);
      navigate(`/panunnuan/room/${roomId}`);
    }
    if (action === "lock") {
      await api("POST", `/api/rooms/${roomId}/lock`);
      appendLog(`locked ${roomId}`);
    }
    if (action === "start") {
      const seedWord = prompt("Seed word", "babuy") || "";
      const res = await api("POST", `/api/rooms/${roomId}/start`, { seedWord });
      patchForm("roomId", roomId);
      patchForm("gameId", res.gameId);
      appendLog(`game ${res.gameId} started`);
    }
    await refreshRooms();
  }

  function connectWs() {
    if (!wsUrl) return;
    if (window.__kaputWs) {
      window.__kaputWs.close();
    }
    const ws = new WebSocket(wsUrl);
    setWsState("connecting");
    ws.onopen = () => {
      setWsState("open");
      appendLog("ws connected");
    };
    ws.onclose = () => {
      setWsState("closed");
      appendLog("ws closed");
    };
    ws.onerror = () => {
      setWsState("error");
      appendLog("ws error");
    };
    ws.onmessage = (evt) => {
      setMessages((prev) => [evt.data, ...prev].slice(0, 100));
    };
    window.__kaputWs = ws;
  }

  function disconnectWs() {
    if (window.__kaputWs) {
      window.__kaputWs.close();
      window.__kaputWs = null;
      setWsState("closed");
    }
  }

  async function submitWord(asPending) {
    if (!form.gameId || !form.phrase) return;
    const route = asPending ? `/api/games/${form.gameId}/submit-pending` : `/api/games/${form.gameId}/submit`;
    const res = await api("POST", route, { phrase: form.phrase });
    appendLog(`word=${form.phrase}, type=${res.validationType}`);
  }

  async function loadResult() {
    if (!form.gameId) return;
    const res = await api("GET", `/api/games/${form.gameId}/result`);
    setGameResult(JSON.stringify(res, null, 2));
  }

  async function saveService(slug) {
    await api("PATCH", `/api/admin/services/${slug}`, {
      ...serviceDrafts[slug],
      sortOrder: Number(serviceDrafts[slug]?.sortOrder || 0)
    });
    appendLog(`saved ${slug}`);
    await refreshServices();
  }

  async function uploadCover(slug, file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("cover", file);
    await api("POST", `/api/admin/services/${slug}/cover`, fd, true);
    appendLog(`uploaded cover ${slug}`);
    await refreshServices();
  }

  async function bootstrapAdmin() {
    await api("POST", "/api/admin/bootstrap", {
      email: form.bootstrapEmail,
      key: form.bootstrapKey
    });
    appendLog(`admin bootstrap updated: ${form.bootstrapEmail}`);
    await refreshMe();
  }

  function patchServiceDraft(slug, key, value) {
    setServiceDrafts((prev) => ({
      ...prev,
      [slug]: { ...(prev[slug] || {}), [key]: value }
    }));
  }

  const navKey = path.startsWith("/panunnuan")
    ? "/panunnuan/"
    : path.startsWith("/sikawma")
      ? "/sikawma/"
      : path.startsWith("/settings")
        ? "/settings/"
        : path.startsWith("/admin/services")
          ? "/admin/services/"
          : "/";
  const roomMatch = path.match(/^\/panunnuan\/room\/([^/]+)$/);
  const roomIdFromPath = roomMatch?.[1] || null;
  const currentRoom = rooms.find((room) => room.id === (roomIdFromPath || form.roomId)) || null;

  useEffect(() => {
    if (roomIdFromPath && form.roomId !== roomIdFromPath) {
      patchForm("roomId", roomIdFromPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomIdFromPath]);

  return (
    <main className="kaput-stage">
      {/* Create Room Modal - 頂層渲染確保彈窗正常 */}
      <Modal
        isOpen={isCreateRoomOpen}
        onClose={onCreateRoomClose}
        placement="center"
        size="lg"
        scrollBehavior="inside"
        backdrop="blur"
      >
        <ModalContent>
          <ModalHeader>建立遊戲房間</ModalHeader>
          <ModalBody>
            <div className="grid gap-3">
              <Input
                classNames={{ inputWrapper: "touch-input" }}
                label="房間名稱"
                placeholder="例如：今晚練習場"
                value={form.roomName}
                onChange={(e) => patchForm("roomName", e.target.value)}
              />
              <Input
                classNames={{ inputWrapper: "touch-input" }}
                type="number"
                label="每棒秒數"
                value={form.answerTimeLimit}
                onChange={(e) => patchForm("answerTimeLimit", e.target.value)}
              />
              <Input
                classNames={{ inputWrapper: "touch-input" }}
                type="number"
                label="容錯次數"
                value={form.maxMistakes}
                onChange={(e) => patchForm("maxMistakes", e.target.value)}
              />
              <div className="flex gap-2">
                <Button size="sm" variant="flat" className="flex-1" onPress={() => patchForm("answerTimeLimit", "20")}>
                  快速 20s
                </Button>
                <Button size="sm" variant="flat" className="flex-1" onPress={() => patchForm("answerTimeLimit", "30")}>
                  標準 30s
                </Button>
                <Button size="sm" variant="flat" className="flex-1" onPress={() => patchForm("answerTimeLimit", "45")}>
                  思考 45s
                </Button>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onCreateRoomClose} isDisabled={isCreatingRoom}>取消</Button>
            <Button
              color="primary"
              className="font-bold"
              isLoading={isCreatingRoom}
              isDisabled={isCreatingRoom}
              onPress={() => void runAction("create room", createRoom)}
            >
              {isCreatingRoom ? "建立中…" : "建立遊戲房間"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <div className="mx-auto grid w-full max-w-6xl gap-4 px-3 pb-24 pt-4 sm:pb-10 sm:px-5 sm:pt-6">
        {/* Desktop header */}
        <header className="hidden sm:block kaput-panel rounded-2xl px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">KAPUT</h1>
              <p className="mt-1 text-sm text-white/75 sm:text-base">kaput.lawlawa.org multi-service temunaw</p>
            </div>
            <div className="pb-1">
              <div className="flex min-w-max overflow-x-auto gap-2 px-1">
                {NAV_ITEMS.map((item) => (
                  <Button
                    key={item.key}
                    radius="full"
                    size="md"
                    color={navKey === item.key ? "primary" : "default"}
                    variant={navKey === item.key ? "solid" : "flat"}
                    className="h-11 min-w-[112px] border border-white/30 bg-white/20 text-sm font-bold"
                    onPress={() => navigate(item.key)}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* Mobile: compact header (title only) */}
        <header className="kaput-panel rounded-2xl px-4 py-3 sm:hidden">
          <h1 className="text-xl font-black tracking-tight">KAPUT</h1>
          <p className="mt-0.5 text-xs text-white/75">kaput.lawlawa.org</p>
        </header>

        {/* Mobile Bottom Nav */}
        <nav className="kaput-bottom-nav fixed bottom-0 left-0 right-0 z-50 sm:hidden">
          <div className="flex items-center justify-around border-t border-white/20 bg-black/40 backdrop-blur-xl">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = navKey === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.key)}
                  className={`flex flex-1 flex-col items-center gap-1 py-3 transition-colors ${
                    isActive ? "text-kaput-cyan" : "text-white/70 active:text-white"
                  }`}
                >
                  <Icon size={22} strokeWidth={2} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {actionError && (
          <div className="kaput-panel rounded-xl border-red-300/50 bg-red-500/20 px-4 py-3 text-sm text-red-100">
            {actionError}
          </div>
        )}

        {path === "/" && (
          <section className="kaput-card-grid">
            {services.length === 0 && (
              <div className="kaput-panel rounded-2xl p-6 text-center text-white/80">
                載入服務列表中…
              </div>
            )}
            {services.map((service, index) => (
              <motion.button
                key={service.serviceSlug}
                type="button"
                className="kaput-service-tile text-left"
                onClick={() => navigate(service.serviceSlug === "panunnuan" ? "/panunnuan/" : "/sikawma/")}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24, delay: Math.min(index * 0.05, 0.25) }}
                whileTap={{ scale: 0.98, opacity: 0.9 }}
              >
                {service.coverUrl ? (
                  <img className="kaput-cover" src={service.coverUrl} alt={service.title} />
                ) : (
                  <div className="kaput-cover" />
                )}
                <div className="flex h-full flex-col bg-black/20 p-4 sm:p-5">
                  <h2 className="text-xl font-black sm:text-2xl">{service.title}</h2>
                  <p className="mt-2 text-sm text-white/90 sm:text-base">{service.subtitle}</p>
                  <p className="mt-2 text-sm text-white/75">{service.description}</p>
                  <div className="mt-auto flex flex-col items-stretch gap-2 pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        service.status === "active" ? "bg-success/30 text-success" : "bg-warning/30 text-warning"
                      }`}
                    >
                      {service.status}
                    </span>
                    <span className="inline-flex h-11 items-center justify-center rounded-xl bg-kaput-cyan font-bold text-kaput-deep sm:min-w-[112px] sm:w-auto">
                      Enter
                    </span>
                  </div>
                </div>
              </motion.button>
            ))}
          </section>
        )}

        {path.startsWith("/sikawma") && (
          <motion.section className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(0)}>
            <h2 className="text-2xl font-black">Sikawma</h2>
            <p className="mt-2 text-white/80">Coming Soon. 路由與封面管理已保留。</p>
            <Button color="secondary" className="mt-4 h-11 min-w-[120px] font-bold" onPress={() => navigate("/")}>
              Back Temunaw
            </Button>
          </motion.section>
        )}

        {path.startsWith("/settings") && (
          <motion.section className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(0)}>
            <h2 className="text-2xl font-black">主題設定</h2>
            <p className="mt-2 text-white/80">選擇你喜歡的配色，設定會儲存在本機。</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {THEME_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => applyTheme(opt.id)}
                  className={`kaput-panel flex flex-col items-start rounded-2xl border-2 p-4 text-left transition-all ${
                    theme === opt.id ? "border-kaput-cyan ring-2 ring-kaput-cyan/50" : "border-white/20 hover:border-white/40"
                  }`}
                >
                  <span className="text-lg font-bold">{opt.label}</span>
                  <span className="mt-1 text-sm text-white/70">{opt.desc}</span>
                  {theme === opt.id && <span className="mt-2 text-xs text-kaput-cyan">目前使用</span>}
                </button>
              ))}
            </div>
          </motion.section>
        )}

        {path.startsWith("/panunnuan") && roomIdFromPath && (
          <RoomView
            roomId={roomIdFromPath}
            currentRoom={currentRoom}
            form={form}
            patchForm={patchForm}
            identity={identity}
            navigate={navigate}
            runAction={runAction}
            roomAction={roomAction}
            refreshRooms={refreshRooms}
            wsState={wsState}
            connectWs={connectWs}
            disconnectWs={disconnectWs}
            submitWord={submitWord}
          />
        )}

        {path.startsWith("/panunnuan") && !roomIdFromPath && (
          <section className="grid gap-4">
            <motion.section className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(0)}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-3xl font-black">Panunnuan</h2>
                  <p className="mt-1 text-sm text-white/80">噶瑪蘭語接龍遊戲大廳</p>
                </div>
                <div className="flex gap-2">
                  <Chip color="success" variant="flat">
                    免登入遊玩（訪客）
                  </Chip>
                  <Chip color={wsState === "open" ? "success" : "default"} variant="flat">
                    WS: {wsState}
                  </Chip>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 py-2">
                <span className="text-sm text-white/90">目前身份：{identity?.displayName || "載入中..."}</span>
                {identity?.type === "guest" && (
                  <Button size="sm" color="primary" variant="flat" onPress={() => { patchForm("guestNickname", identity?.displayName || ""); onNicknameOpen(); }}>
                    設定暱稱
                  </Button>
                )}
                <Button size="sm" color="danger" variant="flat" onPress={() => void runAction("reset guest", logout)}>
                  重置訪客身份
                </Button>
              </div>
              <Modal isOpen={isNicknameOpen} onClose={onNicknameClose} placement="center">
                <ModalContent>
                  <ModalHeader>設定暱稱</ModalHeader>
                  <ModalBody>
                    <Input
                      classNames={{ inputWrapper: "touch-input" }}
                      label="暱稱"
                      placeholder="輸入你的暱稱（1-24 字元）"
                      value={form.guestNickname}
                      onChange={(e) => patchForm("guestNickname", e.target.value)}
                      maxLength={24}
                    />
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="light" onPress={onNicknameClose}>取消</Button>
                    <Button color="primary" onPress={() => void runAction("set nickname", updateGuestNickname)}>儲存</Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </motion.section>

            <motion.section className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(1)}>
              <h3 className="mb-2 text-xl font-extrabold">快速建房</h3>
              <p className="mb-3 text-sm text-white/80">訪客也可以建立房間，直接開始遊戲。</p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCreateRoomOpen(); }}
                  className="z-0 inline-flex h-12 min-w-[160px] items-center justify-center rounded-medium bg-primary px-4 text-base font-black text-primary-foreground transition-opacity hover:opacity-hover"
                >
                  快速建房（彈窗）
                </button>
                <Button color="secondary" className="h-12 font-bold" onPress={() => void runAction("refresh rooms", refreshRooms)}>
                  重新整理房間
                </Button>
              </div>
              <div className="mt-4 grid gap-3 rounded-xl border border-white/20 bg-white/5 p-4">
                <p className="text-sm font-medium text-white/90">或直接填寫建立：</p>
                <Input
                  classNames={{ inputWrapper: "touch-input" }}
                  label="房間名稱"
                  placeholder="例如：今晚練習場"
                  value={form.roomName}
                  onChange={(e) => patchForm("roomName", e.target.value)}
                />
                <div className="flex gap-2">
                  <Button size="sm" variant="flat" className="flex-1" onPress={() => patchForm("answerTimeLimit", "20")}>20s</Button>
                  <Button size="sm" variant="flat" className="flex-1" onPress={() => patchForm("answerTimeLimit", "30")}>30s</Button>
                  <Button size="sm" variant="flat" className="flex-1" onPress={() => patchForm("answerTimeLimit", "45")}>45s</Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <Input
                    classNames={{ inputWrapper: "touch-input" }}
                    type="number"
                    label="容錯次數"
                    value={form.maxMistakes}
                    onChange={(e) => patchForm("maxMistakes", e.target.value)}
                  />
                  <div className="flex items-end">
                    <Button
                      color="primary"
                      className="h-11 w-full font-bold sm:min-w-[100px]"
                      isLoading={isCreatingRoom}
                      isDisabled={isCreatingRoom}
                      onPress={() => void runAction("create room", createRoom)}
                    >
                      {isCreatingRoom ? "建立中…" : "建立"}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.section>

            <motion.section className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(2)}>
              <h3 className="mb-2 text-xl font-extrabold">我的目前房間狀態</h3>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <Input
                  classNames={{ inputWrapper: "touch-input" }}
                  label="房間代碼"
                  placeholder="輸入代碼直接加入，例如 MRAW123"
                  value={form.joinRoomCode}
                  onChange={(e) => patchForm("joinRoomCode", e.target.value.toUpperCase())}
                />
                <Button color="primary" className="h-11 w-full sm:mt-6 sm:min-w-[140px]" onPress={() => void runAction("join by code", joinByCode)}>
                  代碼加入
                </Button>
              </div>
              <div className="mt-3 rounded-xl border border-white/20 bg-black/20 p-3 text-sm text-white/90">
                {currentRoom ? (
                  <p>
                    目前房間：<span className="font-bold">{currentRoom.name}</span>（{currentRoom.id} / {currentRoom.status}）
                  </p>
                ) : (
                  <p>尚未加入房間。可以先建房，或從下方房間列表加入。</p>
                )}
              </div>
              {currentRoom && (
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <Button className="h-11 w-full" onPress={() => void runAction("join room", () => roomAction("join", currentRoom.id))}>
                    再次進入
                  </Button>
                  <Button className="h-11 w-full" onPress={() => void runAction("lock room", () => roomAction("lock", currentRoom.id))}>
                    鎖房
                  </Button>
                  <Button color="warning" className="h-11 w-full font-bold" onPress={() => void runAction("start room", () => roomAction("start", currentRoom.id))}>
                    開始遊戲
                  </Button>
                </div>
              )}
            </motion.section>

            <motion.section className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(3)}>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xl font-extrabold">可加入房間</h3>
                <Button size="sm" variant="light" onPress={() => void runAction("refresh rooms", refreshRooms)}>
                  Refresh
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {rooms.map((room, index) => (
                  <Card
                    key={room.id}
                    className={`border-0 text-white ${
                      index % 4 === 0
                        ? "bg-kaput-red"
                        : index % 4 === 1
                          ? "bg-kaput-blue"
                          : index % 4 === 2
                            ? "bg-kaput-yellow text-black"
                            : "bg-kaput-green"
                    }`}
                  >
                    <CardHeader className="flex-col items-start pb-1">
                      <p className="text-lg font-black">{room.name}</p>
                      <p className="text-xs opacity-90">{room.id}</p>
                    </CardHeader>
                    <CardBody className="pt-0">
                      <div className="flex items-center justify-between">
                        <Chip size="sm" color="default" className="bg-black/20 text-white">
                          {room.status}
                        </Chip>
                        <span className="text-xs opacity-90">{room.answerTimeLimit}s / {room.maxMistakes} mistakes</span>
                      </div>
                    </CardBody>
                    <CardFooter className="grid grid-cols-3 gap-2">
                      <Button size="sm" className="h-10 bg-white/25 text-white" onPress={() => void runAction("join room", () => roomAction("join", room.id))}>
                        Join
                      </Button>
                      <Button size="sm" className="h-10 bg-white/25 text-white" onPress={() => void runAction("lock room", () => roomAction("lock", room.id))}>
                        Lock
                      </Button>
                      <Button size="sm" className="h-10 bg-white/25 text-white" onPress={() => void runAction("start room", () => roomAction("start", room.id))}>
                        Start
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </motion.section>

            <motion.section className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(4)}>
              <Accordion selectionMode="multiple" className="gap-3">
                <AccordionItem
                  key="debug"
                  aria-label="debug"
                  title={<span className="font-bold">進階工具（WebSocket / 測試提交 / Logs）</span>}
                  classNames={{ content: "pb-2" }}
                >
                  <div className="grid gap-3">
                    <div className="grid gap-2 md:grid-cols-[1fr_auto_auto]">
                      <Input
                        classNames={{ inputWrapper: "touch-input" }}
                        label="Room ID"
                        value={form.roomId}
                        onChange={(e) => patchForm("roomId", e.target.value)}
                      />
                      <Button color="primary" className="h-11 min-w-[120px] font-bold" onPress={connectWs}>
                        Connect
                      </Button>
                      <Button color="danger" className="h-11 min-w-[120px] font-bold" onPress={disconnectWs}>
                        Disconnect
                      </Button>
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                      <Input
                        classNames={{ inputWrapper: "touch-input" }}
                        label="Game ID"
                        value={form.gameId}
                        onChange={(e) => patchForm("gameId", e.target.value)}
                      />
                      <Input
                        classNames={{ inputWrapper: "touch-input" }}
                        label="Phrase"
                        value={form.phrase}
                        onChange={(e) => patchForm("phrase", e.target.value)}
                      />
                    </div>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <Button color="primary" className="h-11 w-full font-bold" onPress={() => void runAction("submit word", () => submitWord(false))}>
                        Submit
                      </Button>
                      <Button color="secondary" className="h-11 w-full font-bold" onPress={() => void runAction("submit pending", () => submitWord(true))}>
                        Submit Pending
                      </Button>
                      <Button color="warning" className="h-11 w-full font-bold" onPress={() => void runAction("load result", loadResult)}>
                        Load Result
                      </Button>
                    </div>
                    <CodeBlock text={messages.join("\n")} emptyText="No websocket events yet." />
                    <CodeBlock text={gameResult} emptyText="No game result loaded." />
                    <CodeBlock text={logs.join("\n")} emptyText="No logs yet." />
                  </div>
                </AccordionItem>
              </Accordion>
            </motion.section>
          </section>
        )}

        {path.startsWith("/admin/services") && (
          <section className="grid gap-4">
            <section className="kaput-panel rounded-2xl p-4 sm:p-5">
              <div className="grid gap-3">
                <p className="text-sm text-white/85">
                  Admin required for cover upload and service update. Current identity:
                  {" "}
                  <span className="font-semibold">{identity?.displayName || "-"}</span>
                  {" / "}
                  <span className="font-semibold">{identity?.isAdmin ? "admin" : "non-admin"}</span>
                </p>
                {!identity?.isAdmin && (
                  <div className="grid gap-2 md:grid-cols-3">
                    <Input
                      classNames={{ inputWrapper: "touch-input" }}
                      label="Bootstrap Email"
                      value={form.bootstrapEmail}
                      onChange={(e) => patchForm("bootstrapEmail", e.target.value)}
                    />
                    <Input
                      classNames={{ inputWrapper: "touch-input" }}
                      label="ADMIN_BOOTSTRAP_KEY"
                      type="password"
                      value={form.bootstrapKey}
                      onChange={(e) => patchForm("bootstrapKey", e.target.value)}
                    />
                    <Button color="warning" className="h-11 w-full font-bold" onPress={() => void runAction("bootstrap admin", bootstrapAdmin)}>
                      Become Admin
                    </Button>
                  </div>
                )}
              </div>
            </section>
            {services.map((service, index) => {
              const draft = serviceDrafts[service.serviceSlug] || {};
              return (
                <motion.section key={service.serviceSlug} className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(index)}>
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <h2 className="text-2xl font-black">{service.serviceSlug}</h2>
                    <Chip color={draft.status === "active" ? "success" : "warning"} variant="flat">
                      {draft.status || "coming_soon"}
                    </Chip>
                  </div>
                  <div className="grid gap-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <Input
                        classNames={{ inputWrapper: "touch-input" }}
                        label="Title"
                        value={draft.title || ""}
                        onChange={(e) => patchServiceDraft(service.serviceSlug, "title", e.target.value)}
                      />
                      <Input
                        classNames={{ inputWrapper: "touch-input" }}
                        label="Subtitle"
                        value={draft.subtitle || ""}
                        onChange={(e) => patchServiceDraft(service.serviceSlug, "subtitle", e.target.value)}
                      />
                    </div>
                    <Textarea
                      label="Description"
                      minRows={4}
                      value={draft.description || ""}
                      onChange={(e) => patchServiceDraft(service.serviceSlug, "description", e.target.value)}
                    />
                    <div className="grid gap-3 md:grid-cols-2">
                      <Select
                        label="Status"
                        selectedKeys={[draft.status || "coming_soon"]}
                        onSelectionChange={(keys) =>
                          patchServiceDraft(service.serviceSlug, "status", firstSelection(keys) || "coming_soon")
                        }
                      >
                        {SERVICE_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.key}>{option.label}</SelectItem>
                        ))}
                      </Select>
                      <Input
                        classNames={{ inputWrapper: "touch-input" }}
                        type="number"
                        label="Sort Order"
                        value={draft.sortOrder || "0"}
                        onChange={(e) => patchServiceDraft(service.serviceSlug, "sortOrder", e.target.value)}
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Button
                        color="primary"
                        className="h-11 w-full font-bold"
                        onPress={() => void runAction("save service", () => saveService(service.serviceSlug))}
                      >
                        Save Service Content
                      </Button>
                      <label className="kaput-panel flex h-11 cursor-pointer items-center justify-center rounded-xl px-3 text-sm font-semibold text-white">
                        Upload Cover
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => void runAction("upload cover", () => uploadCover(service.serviceSlug, e.target.files?.[0]))}
                        />
                      </label>
                    </div>
                  </div>
                </motion.section>
              );
            })}
          </section>
        )}
      </div>
    </main>
  );
}

function RoomView({
  roomId,
  currentRoom,
  form,
  patchForm,
  identity,
  navigate,
  runAction,
  roomAction,
  refreshRooms,
  wsState,
  connectWs,
  disconnectWs,
  submitWord
}) {
  const [roomDetail, setRoomDetail] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [turnStartedAt, setTurnStartedAt] = useState(null);
  const [typingPreview, setTypingPreview] = useState("");
  const [typingPlayerId, setTypingPlayerId] = useState(null);

  useEffect(() => {
    if (!roomId) return;
    api("GET", `/api/rooms/${roomId}`)
      .then((res) => setRoomDetail(res.room))
      .catch(() => setRoomDetail(null));
  }, [roomId]);

  useEffect(() => {
    if (!form.roomId) return;
    connectWs();
    return () => disconnectWs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.roomId]);

  useEffect(() => {
    if (!window.__kaputWs || wsState !== "open") return;
    const ws = window.__kaputWs;
    const onMsg = (evt) => {
      try {
        const { type, data } = JSON.parse(evt.data || "{}");
        if (type === "turn_started") {
          setTurnStartedAt(data.startedAt || Date.now());
          setTypingPreview("");
          setTypingPlayerId(null);
        }
        if (type === "typing") {
          setTypingPreview(data.text || "");
          setTypingPlayerId(data.identityId || null);
        }
      } catch (_) {}
    };
    ws.addEventListener("message", onMsg);
    return () => ws.removeEventListener("message", onMsg);
  }, [wsState]);

  const answerTimeLimit = currentRoom?.answerTimeLimit ?? 30;
  useEffect(() => {
    if (!turnStartedAt || !form.gameId) {
      setCountdown(null);
      return;
    }
    const tick = () => {
      const elapsed = (Date.now() - turnStartedAt) / 1000;
      const left = Math.max(0, Math.ceil(answerTimeLimit - elapsed));
      setCountdown(left);
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [turnStartedAt, form.gameId, answerTimeLimit]);

  const showGameUI = !!form.gameId;

  if (showGameUI) {
    return (
      <section className="grid gap-4 pb-8">
        <div className="flex items-center justify-between">
          <Button size="sm" variant="light" className="-ml-1" onPress={() => navigate("/panunnuan/")}>
            返回大廳
          </Button>
          <span className="text-sm text-white/70">{currentRoom?.name || roomId}</span>
        </div>

        <div className="kaput-game-layout flex min-h-[60vh] flex-col gap-6">
          <div className={`flex flex-1 flex-col items-center justify-center text-6xl font-black sm:text-8xl ${countdown !== null && countdown <= 5 ? "kaput-countdown-urgent text-red-500" : "text-white"}`}>
            {countdown !== null ? countdown : "—"}
          </div>

          <div className={`kaput-input-block flex flex-col gap-2 rounded-2xl border-2 p-4 ${typingPlayerId && typingPlayerId !== identity?.id ? "border-kaput-cyan/60 bg-gradient-to-br from-gray-300/20 to-emerald-500/20" : "border-white/30 bg-white/10"}`}>
            <p className="text-xs text-white/70">輸入詞彙（下一棒開頭字母）</p>
            <Input
              classNames={{ inputWrapper: "touch-input bg-white/10" }}
              placeholder="輸入詞彙"
              value={form.phrase}
              onChange={(e) => {
                patchForm("phrase", e.target.value);
                if (window.__kaputWs?.readyState === 1) {
                  window.__kaputWs.send(JSON.stringify({ type: "typing", data: { text: e.target.value, identityId: identity?.id } }));
                }
              }}
            />
            <Button color="primary" className="font-bold" onPress={() => void runAction("submit", () => submitWord(false))}>
              送出
            </Button>
          </div>

          <div className="flex justify-center gap-3 overflow-x-auto pb-2">
            {(roomDetail?.players || [{ displayName: identity?.displayName || "我" }]).map((p, i) => (
              <div
                key={p.id || i}
                className={`flex flex-shrink-0 flex-col items-center gap-1 ${typingPlayerId === p.identityId ? "kaput-avatar-typing" : ""}`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-kaput-cyan/40 text-lg font-bold text-white">
                  {(p.displayName || "?")[0]}
                </div>
                <span className="max-w-[64px] truncate text-xs text-white/80">{p.displayName || "玩家"}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="grid gap-4">
      <motion.section className="kaput-panel rounded-2xl p-4 sm:p-5" {...motionLift(0)}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Button size="sm" variant="light" className="mb-2 -ml-1" onPress={() => navigate("/panunnuan/")}>
              返回大廳
            </Button>
            <h2 className="text-2xl font-black">{currentRoom?.name || roomId}</h2>
            <p className="mt-1 text-sm text-white/80">房間代碼：{roomId}</p>
          </div>
          <Chip color={wsState === "open" ? "success" : "default"} variant="flat">
            WS: {wsState}
          </Chip>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="flat" onPress={() => void runAction("connect", connectWs)}>
            Connect WS
          </Button>
          <Button size="sm" variant="flat" color="danger" onPress={disconnectWs}>
            Disconnect
          </Button>
        </div>
        {currentRoom && (
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            <Button className="h-11 w-full" onPress={() => void runAction("join room", () => roomAction("join", currentRoom.id))}>
              再次進入
            </Button>
            <Button className="h-11 w-full" onPress={() => void runAction("lock room", () => roomAction("lock", currentRoom.id))}>
              鎖房
            </Button>
            <Button color="warning" className="h-11 w-full font-bold" onPress={() => void runAction("start room", () => roomAction("start", currentRoom.id))}>
              開始遊戲
            </Button>
          </div>
        )}
      </motion.section>
    </section>
  );
}

function CodeBlock({ text, emptyText = "" }) {
  return (
    <pre className="max-h-64 overflow-auto rounded-xl border border-white/20 bg-black/25 p-3 text-xs leading-5 text-violet-100">
      {text || emptyText}
    </pre>
  );
}

function motionLift(index) {
  return {
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.24, delay: Math.min(index * 0.05, 0.25) }
  };
}

function normalizePath(path) {
  if (!path || path === "") return "/";
  if (path === "/panunnuan") return "/panunnuan/";
  if (path === "/sikawma") return "/sikawma/";
  if (path === "/settings") return "/settings/";
  if (path === "/admin/services") return "/admin/services/";
  return path;
}

function firstSelection(keys) {
  if (!keys) return "";
  if (typeof keys === "string") return keys;
  if (keys instanceof Set) return [...keys][0] || "";
  const values = Array.from(keys);
  return values[0] || "";
}

function mapActionError(message) {
  if (message === "forbidden") return "forbidden (admin required)";
  if (message === "unauthorized") return "session expired, please retry";
  if (message === "auth_failed") return "帳號不存在或密碼錯誤";
  if (message === "invalid_input") return "輸入格式錯誤（請確認欄位與密碼長度）";
  if (message === "room_code_required") return "請先輸入房間代碼";
  if (message === "guest_only") return "僅訪客可設定暱稱";
  if (message === "room_not_found") return "房間不存在";
  if (message === "room_closed") return "房間已關閉，無法加入";
  return message;
}

async function api(method, path, body, isForm = false) {
  const init = { method, credentials: "include", headers: {} };
  if (body !== undefined) {
    if (isForm) {
      init.body = body;
    } else {
      init.headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
  }
  const response = await fetch(path, init);
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw new Error(data.error || `HTTP_${response.status}`);
  }
  return data;
}
